import React, { useState, useEffect, useRef } from 'react';
import { FixedSizeList as List } from 'react-window';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import InfoBox from "./InfoBox.jsx";
import { ActiveInput, ActiveLabel, DisabledInput, DisabledLabel, ErrorInput, ErrorLabel } from "./Util.jsx";

const Combobox = ({
    options,
    label,
    error = false,
    onChange,
    defaultValue = null,
    disabled = false,
    readOnly = false,
    classes = '',
    info = false,
    infotext = '',
    infopos = 'r',
    infoclass = '',
    infosize = 20
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [filter, setFilter] = useState('');
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    // Sincronizza con il valore esterno
    useEffect(() => {
        if (defaultValue !== null) {
            const option = options.find(opt => opt.value === defaultValue);
            if (option) {
                setSelectedOption(option);
                setInputValue(option.label);
                setFilter('');
            }
        } else {
            setSelectedOption(null);
            setInputValue('');
            setFilter('');
        }
    }, [defaultValue, options]);

    // Filtro in base al testo digitato
    const filteredOptions = options
        .filter(opt => opt.label.toLowerCase().includes(filter.toLowerCase()));

    const handleOptionClick = (option) => {
        if (option.value === '') {
            setSelectedOption(null);
            setInputValue('');
            setFilter('');
        } else {
            setSelectedOption(option);
            setInputValue(option.label);
            setFilter('');
        }
        onChange(option.value);
        setIsOpen(false);
    };

    const handleInputChange = (e) => {
        if (disabled || readOnly) return;

        const value = e.target.value;
        setInputValue(value);
        setFilter(value);
        setSelectedOption(null);
        onChange(null);
        setIsOpen(true);
    };

    const handleInputClick = () => {
        if (disabled || readOnly) return;

        if (!isOpen) {
            setFilter('');
            setIsOpen(true);
        }
    };

    const handleClear = () => {
        if (disabled || readOnly) return;

        setSelectedOption(null);
        setInputValue('');
        setFilter('');
        onChange(null);
    };

    // Chiudi se clic fuori
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Renderizza ogni riga della lista virtualizzata
    const Row = ({ index, style }) => {
        const option = filteredOptions[index];
        return (
            <li
                key={option.value}
                style={style}
                onClick={() => handleOptionClick(option)}
                className="text-sm font-normal cursor-pointer hover:bg-gray-200 hover:dark:bg-neutral-800 p-2"
            >
                {option.label}
            </li>
        );
    };

    return (
        <div className={`${classes}`} ref={wrapperRef}>
            <div className="relative w-full">
                <input
                    type="text"
                    ref={inputRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    onClick={handleInputClick}
                    onFocus={handleInputClick}
                    className={`${disabled ? DisabledInput : error ? ErrorInput : ActiveInput} ${readOnly ? 'cursor-default' : disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    placeholder=" "
                    disabled={disabled}
                    readOnly={readOnly}
                />
                <label
                    className={`hover:cursor-text rounded-lg ${disabled ? DisabledLabel : error ? ErrorLabel : ActiveLabel}`}
                    onClick={() => !disabled && inputRef.current.focus()}
                >
                    {label}
                </label>

                {inputValue !== '' && !readOnly && !disabled && (
                    <div className="absolute inset-y-0 right-5 pr-1 flex items-center text-gray-700 dark:text-neutral-300 hover:cursor-pointer" onClick={(e) => { handleClear(); e.stopPropagation(); }}>
                        <X size={20} />
                    </div>
                )}

                <div className="absolute inset-y-0 right-0 flex items-center pr-1 pointer-events-none text-gray-700 dark:text-neutral-300 hover:cursor-pointer">
                    {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>

                {info && (
                    <div className="absolute inset-y-0 -right-6 flex items-center hover:cursor-pointer">
                        <InfoBox text={infotext} position={infopos} size={infosize} classes={infoclass} />
                    </div>
                )}
            </div>

            {isOpen && !disabled && !readOnly && (
                <ul className="w-full bg-gray-100 dark:bg-neutral-900 border border-blue-500 rounded-lg shadow-lg max-h-60 mt-1 overflow-hidden">
                    <List
                        height={Math.min(filteredOptions.length, 5) * 36}
                        itemCount={filteredOptions.length}
                        itemSize={36}
                        width="100%"
                    >
                        {Row}
                    </List>

                    {filteredOptions.length === 0 && (
                        <li className="text-sm text-gray-500 p-2">Nessun risultato</li>
                    )}
                </ul>
            )}
        </div>
    );
};

export default Combobox;